/**
 * Multi-document storage for excalidraw-app.
 *
 * Persists multiple named documents (drawings) in IndexedDB:
 *
 * - a metadata index (`DocumentMeta[]`) under a single key
 * - each document's scene (elements + appState) under its own key
 *
 * Binary files (images) are intentionally NOT stored per-document. They live
 * in the shared `files-db` store (see `data/LocalData.ts`) keyed by unique
 * file id, so multiple documents can reference the same file without
 * duplicating it.
 */

import { createStore, get, set, del } from "idb-keyval";

import type { ExcalidrawElement } from "@excalidraw/element/types";
import type { AppState } from "@excalidraw/excalidraw/types";

export type DocumentMeta = {
  id: string;
  /**
   * Path-like hierarchical name, segments separated by `/`,
   * e.g. `work/client-a/logo`.
   */
  name: string;
  createdAt: number;
  updatedAt: number;
  revision?: number;
  syncState?: "local" | "saving" | "saved" | "offline" | "conflict";
};

export type StoredDocumentData = {
  elements: ExcalidrawElement[];
  appState: Partial<AppState>;
};

const documentsStore = createStore(
  "excalidraw-documents-db",
  "documents-store",
);

const INDEX_KEY = "__index__";

let storageScope = "guest";
const indexSaveQueues = new Map<string, Promise<void>>();
const documentSaveQueues = new Map<string, Promise<void>>();

const scopedKey = (key: string, scope = storageScope) =>
  scope === "guest" ? key : `scope:${scope}:${key}`;

const documentKey = (id: string, scope = storageScope) =>
  scopedKey(`document:${id}`, scope);

export const documentStorage = {
  setScope(scope: string | null) {
    storageScope = scope || "guest";
  },

  getScope() {
    return storageScope;
  },

  async loadIndex(scope = storageScope): Promise<DocumentMeta[]> {
    try {
      const index = await get<DocumentMeta[]>(
        scopedKey(INDEX_KEY, scope),
        documentsStore,
      );
      return Array.isArray(index) ? index : [];
    } catch (error: any) {
      console.error(error);
      return [];
    }
  },

  saveIndex(index: DocumentMeta[], scope = storageScope) {
    const key = scopedKey(INDEX_KEY, scope);
    const previous = indexSaveQueues.get(key) || Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(() => set(key, index, documentsStore))
      .finally(() => {
        if (indexSaveQueues.get(key) === next) {
          indexSaveQueues.delete(key);
        }
      });
    indexSaveQueues.set(key, next);
    return next;
  },

  loadDocument(id: string, scope = storageScope) {
    return get<StoredDocumentData>(documentKey(id, scope), documentsStore);
  },

  saveDocument(id: string, data: StoredDocumentData, scope = storageScope) {
    const key = documentKey(id, scope);
    const previous = documentSaveQueues.get(key) || Promise.resolve();
    const next = previous
      .catch(() => undefined)
      .then(() => set(key, data, documentsStore))
      .finally(() => {
        if (documentSaveQueues.get(key) === next) {
          documentSaveQueues.delete(key);
        }
      });
    documentSaveQueues.set(key, next);
    return next;
  },

  deleteDocument(id: string, scope = storageScope) {
    return del(documentKey(id, scope), documentsStore);
  },
};
